import { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { VehicleThumb } from '@/components/ui/vehicle-thumb';
import { ListPagination } from '@/components/ListPagination';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, MoreHorizontal, Pencil, Undo2, Trash2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';
import { useServerPagination } from '@/hooks/useServerPagination';
import { DEPOSITS_PAGE_SIZE, useContractDepositsList } from '@/hooks/useContractDeposits';
import {
    REFUND_EPSILON,
    UNASSIGNED_BADGE_STYLE,
    getDepositStatus,
    getDepositStatusBadge,
    remainingRefundable,
} from '@/lib/depositStatus';
import { businessDay, formatCurrency } from '@/lib/format';
import EditDepositSheet from '@/components/contracts/deposits/EditDepositSheet';
import RefundDepositSheet from '@/components/contracts/deposits/RefundDepositSheet';
import type { DepositRow, DepositStatusFilter } from '@/components/contracts/deposits/types';

const FILTER_OPTIONS: { value: DepositStatusFilter; label: string }[] = [
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'all', label: 'All Deposits' },
];

const EMPTY_STATES: Record<DepositStatusFilter, { title: string; sub: string }> = {
    unassigned: {
        title: 'No unassigned deposits',
        sub: 'Every recorded deposit is linked to a contract or refunded.',
    },
    assigned: {
        title: 'No assigned deposits yet',
        sub: 'Link a deposit to a contract from Contract Setup → Financials.',
    },
    refunded: {
        title: 'No refunds recorded yet',
        sub: 'Refund a deposit from its row menu — the DEBIT transaction is assigned to the deposit.',
    },
    all: {
        title: 'No deposits recorded yet',
        sub: 'Record a deposit to link an incoming payment to a driver.',
    },
};

interface ContractDepositsRecordsProps {
    /** Opens the page-level Record Deposit sheet (the empty-state CTA). */
    onRecordDeposit?: () => void;
}

export default function ContractDepositsRecords({ onRecordDeposit }: ContractDepositsRecordsProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    // The action queue is the default view: deposits nobody has dealt with yet.
    const [statusFilter, setStatusFilter] = useState<DepositStatusFilter>('unassigned');

    const [editDeposit, setEditDeposit] = useState<DepositRow | null>(null);
    const [editStartInDelete, setEditStartInDelete] = useState(false);
    const [refundDeposit, setRefundDeposit] = useState<DepositRow | null>(null);

    // total_count rides on every RPC row — no separate count query.
    const [totalCount, setTotalCount] = useState(0);

    const { currentPage, totalPages, totalItems, goToPage, resetPage } = useServerPagination({
        totalCount,
        itemsPerPage: DEPOSITS_PAGE_SIZE,
    });

    const { data, isLoading, isFetching } = useContractDepositsList({
        page: currentPage,
        search: debouncedSearch,
        status: statusFilter,
    });

    useEffect(() => {
        if (data?.totalCount !== undefined) setTotalCount(data.totalCount);
    }, [data?.totalCount]);

    useEffect(() => { resetPage(); }, [debouncedSearch, statusFilter]);

    const deposits = data?.rows ?? [];

    const handleOpenEdit = (deposit: DepositRow, startInDelete = false) => {
        setEditStartInDelete(startInDelete);
        setEditDeposit(deposit);
    };

    const handleCloseEdit = () => {
        setEditDeposit(null);
        setEditStartInDelete(false);
    };

    const handleManageRefunds = () => {
        const deposit = editDeposit;
        handleCloseEdit();
        setRefundDeposit(deposit);
    };

    const getDriverName = (d: DepositRow) =>
        [d.driver_first_name, d.driver_middle_name, d.driver_last_name].filter(Boolean).join(' ') || 'N/A';

    const getDriverInitials = (d: DepositRow) =>
        `${(d.driver_first_name || '')[0] || ''}${(d.driver_last_name || '')[0] || ''}` || '?';

    const emptyState = EMPTY_STATES[statusFilter];

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search driver, phone, plate, description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DepositStatusFilter)}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Assignment" />
                        </SelectTrigger>
                        <SelectContent>
                            {FILTER_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {!isLoading && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={goToPage}
                    />
                )}
            </div>

            {isLoading ? (
                <div className="space-y-3">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            ) : deposits.length > 0 ? (
                <div className={`overflow-x-auto transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Driver</TableHead>
                                <TableHead>Vehicle</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="w-12" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {deposits.map((d) => {
                                const unassigned = d.contract_id === null;
                                const bankDate = businessDay(d.transaction_at ?? d.created_at)!;
                                const recordedDate = businessDay(d.created_at)!;
                                const showRecorded =
                                    format(bankDate, 'yyyy-MM-dd') !== format(recordedDate, 'yyyy-MM-dd');
                                const badge = getDepositStatusBadge(getDepositStatus(d.amount, d.refunded_total));
                                const refunded = d.refunded_total > REFUND_EPSILON;
                                const remaining = remainingRefundable(d.amount, d.refunded_total);

                                return (
                                    <TableRow
                                        key={d.id}
                                        className="group cursor-pointer"
                                        onClick={() => handleOpenEdit(d)}
                                    >
                                        <TableCell className="text-muted-foreground">
                                            <p>{format(bankDate, 'MMM d, yyyy')}</p>
                                            {showRecorded && (
                                                <p className="text-xs text-muted-foreground/70">
                                                    Recorded {format(recordedDate, 'MMM d')}
                                                </p>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {!unassigned ? (
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-8 w-8">
                                                        <SignedAvatarImage src={d.driver_photo || undefined} alt={getDriverName(d)} />
                                                        <AvatarFallback className="text-[10px] bg-muted">{getDriverInitials(d)}</AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium">{getDriverName(d)}</p>
                                                        <p className="text-xs text-muted-foreground">{d.driver_phone || ''}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-1">
                                                    {d.remarks ? (
                                                        <p className="text-sm text-muted-foreground whitespace-pre-line">{d.remarks}</p>
                                                    ) : (
                                                        <p className="text-sm italic text-muted-foreground/70">— no depositor details —</p>
                                                    )}
                                                    <Badge variant="outline" className={UNASSIGNED_BADGE_STYLE}>Unassigned</Badge>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <VehicleThumb
                                                registration={d.vehicle_registration}
                                                vin={d.vehicle_vin}
                                                make={d.vehicle_make}
                                                model={d.vehicle_model}
                                                color={d.vehicle_color}
                                                listingUrl={d.vehicle_listing_url}
                                                updatedAt={d.vehicle_updated_at}
                                            />
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-xs max-w-[300px]">
                                            <span className="whitespace-normal break-all">{d.description || '-'}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <p className="font-medium tabular-nums">{formatCurrency(d.amount, d.currency)}</p>
                                            {refunded && (
                                                <p className="text-xs tabular-nums">
                                                    <span className="text-red-600">−{formatCurrency(d.refunded_total, d.currency)}</span>
                                                    <span className="text-muted-foreground"> refunded · {formatCurrency(remaining, d.currency)} held</span>
                                                </p>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" />
                                                        <span className="sr-only">Actions</span>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                                    <DropdownMenuItem onClick={() => handleOpenEdit(d)}>
                                                        <Pencil className="h-4 w-4 mr-2" />
                                                        Edit Deposit
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => setRefundDeposit(d)}>
                                                        <Undo2 className="h-4 w-4 mr-2" />
                                                        {d.refund_count > 0 ? `Refunds (${d.refund_count})` : 'Record Refund'}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        onClick={() => handleOpenEdit(d, true)}
                                                        className="text-destructive focus:text-destructive"
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            ) : debouncedSearch ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <p className="text-muted-foreground">No deposits match "{debouncedSearch}"</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">
                        Search covers depositor remarks, driver name and phone, plate and transaction description.
                    </p>
                </div>
            ) : (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <p className="text-muted-foreground">{emptyState.title}</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">{emptyState.sub}</p>
                    {statusFilter === 'all' && onRecordDeposit && (
                        <Button className="mt-4" onClick={onRecordDeposit}>
                            <Plus className="h-4 w-4 mr-2" />
                            Record Deposit
                        </Button>
                    )}
                </div>
            )}

            <EditDepositSheet
                deposit={editDeposit}
                onOpenChange={(open) => { if (!open) handleCloseEdit(); }}
                startInDeleteConfirm={editStartInDelete}
                onManageRefunds={handleManageRefunds}
            />

            <RefundDepositSheet
                deposit={refundDeposit}
                onOpenChange={(open) => { if (!open) setRefundDeposit(null); }}
            />
        </div>
    );
}
